import { ReducerCreators, PayloadAction } from '@reduxjs/toolkit';

import { AccessState } from './types';
import { GetChannelAccess } from '@/connection/access/get-channel-access';
import { GrantAccess } from '@/connection/access/grant-access';
import { RevokeAccess } from '@/connection/access/revoke-access';
import { ReplaceUserAccess } from '@/connection/access/replace-access';
import { requestDataToUserAccess } from './utils';
import { EPermissions } from '@/types/permissions.types';
import { Access, UserAccess } from '@/access/access';

export const reducers = (create: ReducerCreators<AccessState>) => ({
  getUsers: create.asyncThunk(
    async (
      {
        channel,
        page,
        limit,
        search,
        roleFilter,
      }: { channel: number; page: number; limit?: number; search?: string; roleFilter?: EPermissions | '' },
      { rejectWithValue }
    ) => {
      try {
        const access = await GetChannelAccess(channel, page, limit, search, roleFilter);
        return access;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      pending: (state) => {
        state.gettingUsers = true;
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting users';
      },
      fulfilled: (state, action) => {
        state.users = (action.payload?.data ?? []).map(requestDataToUserAccess);
        state.page = 1;
        state.totalUsers = action.payload?.total ?? 0;
      },

      settled: (state) => {
        state.gettingUsers = false;
      },
    }
  ),
  getMoreUsers: create.asyncThunk(
    async (
      {
        channel,
        page,
        limit,
        search,
        roleFilter,
      }: { channel: number; page: number; limit?: number; search?: string; roleFilter?: EPermissions | '' },
      { rejectWithValue }
    ) => {
      try {
        const access = await GetChannelAccess(channel, page, limit, search, roleFilter);
        return access;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      pending: (state) => {
        state.gettingUsers = true;
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting users';
      },
      fulfilled: (state, action) => {
        state.users = (action.payload?.data ?? []).map(requestDataToUserAccess);
        state.page = state.page + 1;
        state.totalUsers = action.payload?.total ?? 0;
      },

      settled: (state) => {
        state.gettingUsers = false;
      },
    }
  ),
  grantUserAccess: create.asyncThunk(
    async (
      { channel, email, accesses }: { channel: number; email: string; accesses: Access[] },
      { rejectWithValue }
    ) => {
      try {
        const access = await GrantAccess(
          channel,
          email,
          accesses.map((access) => access.toResourceRequest())
        );
        return access;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      pending: (state, action) => {
        state.updatingUser = action.meta?.arg.email ?? '';
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting users';
      },
      fulfilled: (state, action) => {
        state.users = state.users.map((user) => {
          if (user.userId === action.meta?.arg.email) {
            user.permissions = [...user.permissions, ...action.meta.arg.accesses];
          }
          return user;
        });
      },
      settled: (state) => {
        state.updatingUser = '';
      },
    }
  ),
  revokeAccess: create.asyncThunk(
    async ({ channel, email }: { channel: number; email: string }, { rejectWithValue }) => {
      try {
        const access = await RevokeAccess(channel, email);
        return access;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      pending: (state, action) => {
        state.updatingUser = action.meta?.arg.email ?? '';
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting user';
      },
      fulfilled: (state, action) => {
        state.users = state.users.map((user) => {
          if (user.userId === action.meta?.arg.email) {
            user.permissions = [];
          }
          return user;
        });
      },
      settled: (state) => {
        state.updatingUser = '';
      },
    }
  ),
  replaceAccess: create.asyncThunk(
    async (
      {
        channel,
        email,
        accesses,
        roles,
      }: { channel: number; email: string; accesses: Access[]; roles: EPermissions[] },
      { rejectWithValue }
    ) => {
      try {
        const access = await ReplaceUserAccess(
          channel,
          email,
          accesses.map((access) => access.toString()),
          roles
        );
        return access;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      pending: (state, action) => {
        state.updatingUser = action.meta?.arg.email ?? '';
        state.errorGettingUsers = undefined;
      },
      rejected: (state) => {
        state.errorGettingUsers = 'Error getting user';
      },
      fulfilled: (state, action) => {
        state.users = state.users.map((access) => {
          if (access.userId === action.meta.arg.email) {
            access.permissions = action.meta.arg.accesses;
            access.user.roles = action.meta.arg.roles;
            return UserAccess.from(access as UserAccess);
          }
          return access;
        });
      },
      settled: (state) => {
        state.updatingUser = '';
      },
    }
  ),
  updateAccess: create.reducer((state, action: PayloadAction<UserAccess>) => {
    state.users = state.users.map((user) => {
      if (user.userId === action.payload.userId) {
        // replace class instance with new replaced instance to force reducer to update
        return UserAccess.from(action.payload);
      }
      return user;
    });
  }),
});
